import {
    Box,
    IconButton,
    InputAdornment,
    LinearProgress,
    Popover,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { ASSISTANT_MODES } from '../../constants';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ChatsTabBody from './components/ChatsTabBody';
import CloseIcon from '@mui/icons-material/Close';
import Locale from '/b2b/common/components/Locale';
import { PANEL_MODES } from '../../state/reducer';
import PersonIcon from '@mui/icons-material/PersonOutline';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import makeStyles from '@mui/styles/makeStyles';
import styles from './ChatsPopover.styles';
import { translate } from '/b2b/common/helpers/i18n';
import useAIAssistant from '../../useAIAssistant';

const useStyles = makeStyles(styles);

/*
 * The conversation browser: AI / Privacy Team tabs, search, paginated lists, and
 * the per-row delete affordance. Owns the tab and the search box; the list and its
 * paging come from the provider, and selection and deletion delegate to the shell.
 */
const ChatsPopover = props => {
    const {
        open,
        anchorEl,
        onClose,
        activeConversationId,
        onSelectConversation,
        onDeleteConversation,
    } = props;
    // Note: panelMode is read from props by the styles (max-height fits the pop-up).
    const classes = useStyles(props);
    const {
        conversations,
        conversationsHasNextPage,
        conversationsPage,
        isLoadingConversations,
        loadConversations,
        activeConversation,
    } = useAIAssistant();

    const [activeTab, setActiveTab] = useState(ASSISTANT_MODES.AI);
    const [search, setSearch] = useState('');
    const [searchVisible, setSearchVisible] = useState(false);

    /*
     * Which tab a conversation belongs to. chat-api keeps one list and marks the threads
     * the Privacy Team owns with an escalation, so that field is the split.
     */
    const isPrivacyTeamConversation = c => Boolean(c.escalation);

    /*
     * Hydrate the lists up front so switching tabs is instant. chat-api keeps one list,
     * so one request answers both tabs and both read its paging.
     */
    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    /*
     * Snap the tab to where the active conversation lives so the popover opens on the
     * relevant list. AI is the default (covers ai-mode and a brand-new chat).
     */
    const isActivePrivacyTeam = activeConversation
        ? isPrivacyTeamConversation(activeConversation)
        : false;
    useEffect(() => {
        setActiveTab(isActivePrivacyTeam ? ASSISTANT_MODES.HUMAN : ASSISTANT_MODES.AI);
    }, [isActivePrivacyTeam]);

    // Reset to the hidden default whenever the popover closes.
    useEffect(() => {
        if (!open) {
            setSearch('');
            setSearchVisible(false);
        }
    }, [open]);

    const searchTerm = search.trim().toLowerCase();
    const matchesSearch = c => !searchTerm || c.title?.toLowerCase().includes(searchTerm);
    /*
     * AI is the default; optimistic conversations carrying neither tag are treated as
     * AI so they don't accidentally hide from the user.
     */
    const aiConversations = conversations
        .filter(c => !isPrivacyTeamConversation(c))
        .filter(matchesSearch);
    const humanConversations = conversations
        .filter(isPrivacyTeamConversation)
        .filter(matchesSearch);
    const isAiTab = activeTab === ASSISTANT_MODES.AI;

    const handleTabChange = (_event, value) => {
        setActiveTab(value);
        setSearch('');
    };

    const toggleSearch = () => {
        if (searchVisible) setSearch('');
        setSearchVisible(!searchVisible);
    };

    const handleLoadMore = () => loadConversations({ page: conversationsPage + 1, silent: true });

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            classes={{ paper: classes.popoverPaper }}
        >
            <Box className={classes.popoverHeader}>
                <Typography variant="subtitle1" className={classes.popoverTitle}>
                    <Locale path="aiAssistant.buttons.chats.label" />
                </Typography>
                <Box className={classes.popoverHeaderActions}>
                    <IconButton
                        size="small"
                        onClick={toggleSearch}
                        aria-label={translate('aiAssistant.inputs.searchChats.placeholder')}
                        aria-pressed={searchVisible}
                        sx={{ color: searchVisible ? 'primary.main' : undefined }}
                    >
                        <SearchIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label={translate('common.close')}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                className={classes.popoverTabs}
            >
                <Tab
                    value={ASSISTANT_MODES.AI}
                    icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                    iconPosition="start"
                    label={translate('aiAssistant.name')}
                    className={classes.popoverTab}
                />
                <Tab
                    value={ASSISTANT_MODES.HUMAN}
                    icon={<PersonIcon fontSize="small" />}
                    iconPosition="start"
                    label={translate('aiAssistant.sender.privacyTeam')}
                    className={classes.popoverTab}
                />
            </Tabs>
            {searchVisible && (
                <Box className={classes.popoverSearch}>
                    <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        placeholder={translate('aiAssistant.inputs.searchChats.placeholder')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        inputProps={{
                            autoComplete: 'off',
                            'aria-label': translate('aiAssistant.inputs.searchChats.placeholder'),
                        }}
                    />
                </Box>
            )}
            {isLoadingConversations && <LinearProgress />}
            <ChatsTabBody
                classes={classes}
                conversations={isAiTab ? aiConversations : humanConversations}
                isLoading={isLoadingConversations}
                hasNextPage={conversationsHasNextPage && !searchTerm}
                activeConversationId={activeConversationId}
                emptyMessageKey={
                    isAiTab ? 'aiAssistant.tabs.ai.empty' : 'aiAssistant.tabs.privacyTeam.empty'
                }
                showDelete={isAiTab}
                onSelect={onSelectConversation}
                onDelete={isAiTab ? onDeleteConversation : undefined}
                onLoadMore={handleLoadMore}
            />
        </Popover>
    );
};

ChatsPopover.propTypes = {
    open: PropTypes.bool,
    anchorEl: PropTypes.any,
    onClose: PropTypes.func.isRequired,
    activeConversationId: PropTypes.string,
    onSelectConversation: PropTypes.func.isRequired,
    onDeleteConversation: PropTypes.func.isRequired,
    panelMode: PropTypes.oneOf(Object.values(PANEL_MODES)),
};

export default ChatsPopover;
